/**
 * The feed-gateway transport for this tab (flag: `feedGatewayEnabled()`).
 *
 * EXCLUSIVE with the hydra fleet — `useHydra.ensureFleet` starts one or the
 * other, never both. It emulates exactly what the store expects of a fleet:
 * one registered FeedClient (role `firehose`, accepts everything — with a
 * single feed there is nothing to arbitrate), the global connected / keepalive
 * flags, and the odds-lane health the fair-odds marker greys on.
 *
 * SCOPES ARE DERIVED from declarations the views already make — no view knows
 * the gateway exists:
 *
 *   opNeeds scope is a key list  → the tab is on a GAME page → `game` scope
 *                                  (those keys; every class; odds; game state)
 *   otherwise                    → `board` scope for the sports feedNeeds lists
 *   marketStore book-needs union → `books`
 *   held positions / orders      → `tickers`
 *
 * A game page declares its route-composed key to opNeeds at mount, so the game
 * scope is sent before any row exists — there is no sport-wide "phase 1".
 *
 * Low bandwidth mode (`stores/lowBandwidth`) rides beside the list as `opts`:
 * core rows only, OP without player props, Polymarket alt strikes held. It is
 * re-sent on every flip; the gateway re-sends what the new options change.
 */
import { GatewayClient, type GatewayAlive, type GatewayGameScope } from '../clients/GatewayClient';
import { gatewayHealth } from '@/lib/feed/gatewayHealthMeter';
import type { HydraOpOddsMsg } from '../clients/HydraClient';
import { feedGatewayUrl } from '../clients/feedFilterConfig';
import { accountTickers, UNRESOLVED_POLY_ORDER } from '../stores/accountFeedNeeds';
import { useAccountStore } from '../stores/accountStore';
import { currentBoardKinds, setBoardKindsSink } from '../stores/boardKinds';
import { setRowNeedsSink } from '../stores/rowNeeds';
import { useAuthStore } from '../stores/authStore';
import { expandSportSiblings, setFeedNeedsSink } from '../stores/feedNeeds';
import { useGameStatesStore } from '../stores/gameStatesStore';
import { useMarketStore } from '../stores/marketStore';
import { altBaseSport, normalizeFeedSport } from '../stores/marketFrameNormalization';
import { setGatewayClaims } from '../clients/feedConfig';
import { addOpScopeRawSink } from '../stores/opNeeds';
import { gatewayTabOpts, subscribeLowBandwidth } from '../stores/lowBandwidth';
import { usePolyAccountStore } from '../stores/polyAccountStore';
import type { UnabatedGameState } from '../services/hydraAnalytics';

export const GATEWAY_FEED_ID = 'gateway';
/** Odds lane: no data frame for this long while connected ⇒ `silent`. Matches
 *  the fleet controller's OP_SILENCE_MS so the marker reads the same either way. */
const OP_SILENCE_MS = 120_000;
/** The store fences removals by (socketIncarnation, filterGeneration); the
 *  gateway owns removal outright, so its pair never changes. */
const PAIR = { socketIncarnation: GATEWAY_FEED_ID, filterGeneration: 0 };

const store = () => useMarketStore.getState();

let _client: GatewayClient | null = null;
let _detach: Array<() => void> = [];
let _needSports: Set<string> | null = new Set();
let _opKeys: string[] | null = null;
let _activeSports: string[] = [];

/** Wire sports a game key implies (`nfl|…` → NFL + its split labels). */
function sportsOfKey(key: string): string[] {
  const token = key.split('|')[0]?.trim();
  return token ? expandSportSiblings([token.toUpperCase()]) : [];
}

function publishScopes(): void {
  if (!_client) return;
  const needSports = _needSports == null ? _activeSports : expandSportSiblings(_needSports);
  if (_opKeys != null && _opKeys.length > 0) {
    const sports = new Set(needSports);
    for (const k of _opKeys) for (const s of sportsOfKey(k)) sports.add(s);
    const game: GatewayGameScope = { sports: [...sports], keys: _opKeys };
    _client.setScopes({ boardSports: [], games: [game], opts: gatewayTabOpts() ?? null });
  } else {
    _client.setScopes({ boardSports: needSports, boardKinds: currentBoardKinds(), games: [], opts: gatewayTabOpts() ?? null });
  }
}

/** Rows a view names by TICKER (main's feed-filters-per-view: the Tracker, My
 *  Trades, trader/org detail) ride the same `tickers` scope as held positions. */
let _needTickers: string[] = [];
/** Rows a view wants live beyond its board (`stores/rowNeeds`): the Polymarket
 *  alt lines of the game the Spread/Total board has expanded — held by the
 *  board scope, streamed only while named. */
let _rowTickers: string[] = [];

function publishHeld(): void {
  const held = accountTickers().filter((t) => t !== UNRESOLVED_POLY_ORDER);
  _client?.setScopes({ heldTickers: [...new Set([...held, ..._needTickers, ..._rowTickers])] });
}

function noteAlive(a: GatewayAlive): void {
  store().noteOpFeedHealth(a.oddspapiAgeMs == null || a.oddspapiAgeMs > OP_SILENCE_MS ? 'silent' : 'live');
  if (a.serverTs != null) gatewayHealth.onAlive(a.serverTs);
}

let _shellOwned = false;
let _laneRefs = 0;

export function gatewayActive(): boolean { return _client != null; }
export function gatewayClient(): GatewayClient | null { return _client; }

/** Start the gateway transport. `onGiveUp` fires once if the gateway cannot be
 *  reached — the caller tears this down and starts the fleet instead. */
export function startGateway(onGiveUp: () => void): void {
  _shellOwned = true;
  open(onGiveUp);
}

/** A standalone page (the Sniper screen mounts outside the shell, so no
 *  `useHydra`) holds the gateway through this instead of a per-sport lane.
 *  Refcounted; in the shell it simply rides the transport already running. */
export function acquireGatewayForLane(): () => void {
  _laneRefs += 1;
  open(() => console.warn('[gateway] unreachable from a standalone page — reload to retry'));
  let released = false;
  return () => {
    if (released) return;
    released = true;
    _laneRefs = Math.max(0, _laneRefs - 1);
    if (_laneRefs === 0 && !_shellOwned) close();
  };
}

function open(onGiveUp: () => void): void {
  if (_client) return;
  // Odds arrive as ABSOLUTE per-game frames on one ordered socket — never a
  // vendor snapshot burst (the gateway strips `generation`), so the fleet's
  // OpSnapshotStager, which admits nothing until it has committed a burst,
  // does not apply. What remains of its contract is the per-game rev guard: a
  // frame older than the one held (same producer epoch) is a no-op.
  const opRevs = new Map<string, { epoch: string; rev: number }>();
  const onOpOdds = (msg: HydraOpOddsMsg): void => {
    const key = msg.op?.canonicalGameKeyV2 || msg.op?.canonicalGameKey || msg.ticker;
    if (!key) return;
    const held = opRevs.get(key);
    if (held && held.epoch === msg.sourceEpoch && msg.rev <= held.rev) return;
    opRevs.set(key, { epoch: msg.sourceEpoch, rev: msg.rev });
    if (!Array.isArray(msg.op.markets) || msg.op.markets.length === 0) store().deleteOpOdds(key);
    else store().ingestOpOdds(msg.op);
  };
  const client = new GatewayClient(
    {
      onMarket: (market, age) => { gatewayHealth.onFrame(); store().ingestMarket(market, GATEWAY_FEED_ID, age); },
      onOrderbook: (ticker, book, age, observation) => { gatewayHealth.onFrame(); store().ingestOrderbook(ticker, book, GATEWAY_FEED_ID, age, observation); },
      onOpOdds,
      onGameState: (msg) => {
        if (typeof msg.ticker !== 'string' || !msg.ticker) return;
        useGameStatesStore.getState().applyWireState(
          msg.ticker,
          (msg.state as UnabatedGameState | null) ?? null,
          { rev: msg.rev, sourceEpoch: msg.sourceEpoch, serverTs: msg.serverTs },
        );
        const st = msg.state as { eventStart?: string; statusId?: number } | null;
        if (st?.eventStart) {
          const ms = Date.parse(st.eventStart);
          // #1253: the phase rides beside the start; a state without a valid one seeds nothing (as main's poll).
          if (Number.isFinite(ms) && (st.statusId === 1 || st.statusId === 2 || st.statusId === 3)) store().seedEventStartTimeMsByKey(msg.ticker, ms, st.statusId);
        }
      },
      onActiveSports: (sports) => {
        _activeSports = sports;
        store().ingestActiveSports(sports, GATEWAY_FEED_ID);
        // The gateway's announce IS the fleet directory for this tab: it unions
        // the directory's labels with every upstream's own activeSports. With
        // the gateway as transport the browser never merges the directory
        // itself, so without this the edge-only gate (`sportIsServable`,
        // EDGE_ONLY_SPORTS = NFL) sees no claim for NFL and the toolbar drops
        // the chip while 400+ NFL rows sit in the store. Servability only: a
        // directory claim would also narrow the fallback fleet's firehose.
        setGatewayClaims(sports.map(normalizeFeedSport).filter((s): s is NonNullable<typeof s> => s != null));
        if (_needSports == null) publishScopes(); // an "all sports" board follows the slate
      },
      onRemove: (tickers) => { for (const t of tickers) store().applyMarketRemoved(GATEWAY_FEED_ID, t, PAIR); },
      onRemoveGames: (keys) => { for (const k of keys) { opRevs.delete(k); store().deleteOpOdds(k); } },
      onAlive: noteAlive,
      onPong: (serverTs, sentAtMs) => gatewayHealth.onPong(serverTs, sentAtMs),
      onStatus: (connected) => {
        // Shared US quotes belong to this socket incarnation. Reconnecting
        // cannot make its previous prices current before new frames arrive.
        store().noteUsFeedStatus(GATEWAY_FEED_ID, connected);
        store().setConnected(connected);
        if (!connected) gatewayHealth.reset();
        // A reconnect replays the gateway's cache from scratch — forget the revs
        // so a restarted producer's lower rev is not mistaken for a straggler.
        if (!connected) { opRevs.clear(); store().noteOpFeedHealth('down'); }
      },
      onKeepalive: () => store().noteKeepalive(),
      onGiveUp,
    },
    { url: feedGatewayUrl(), getToken: () => useAuthStore.getState().token ?? null },
  );
  _client = client;
  store().setFeedClients([{ id: GATEWAY_FEED_ID, client, accepts: () => true, role: 'firehose', label: 'GATEWAY', url: feedGatewayUrl(), opAuthority: true, opFullFeed: true }]);
  store().noteOpActiveFeed(GATEWAY_FEED_ID);
  setBoardKindsSink(() => publishScopes());
  setRowNeedsSink((tickers) => { _rowTickers = tickers.slice(0, 8_000); publishHeld(); });
  setFeedNeedsSink((needs) => {
    // ALT pseudo-sports (main's he6/he7 edges) don't exist on SuperHydra; their tokens would only spend
    // the gateway's board-sport budget (scopes.ts caps it). The base sport already carries the rows.
    _needSports = needs.sports == null ? null : new Set([...needs.sports].filter((s) => altBaseSport(s) == null));
    // `tickers: null` = ticker-broad (some view cannot name its rows) — the
    // sports axis above already covers that; a Set names exact rows.
    _needTickers = needs.tickers == null ? [] : [...needs.tickers].slice(0, 8_000);
    publishScopes();
    publishHeld();
  });
  _detach = [
    addOpScopeRawSink((games) => { _opKeys = games == null ? null : [...games]; publishScopes(); }),
    subscribeLowBandwidth(publishScopes),
    useAccountStore.subscribe(publishHeld),
    usePolyAccountStore.subscribe(publishHeld),
    // Sign-in / sign-out re-dials (the socket must carry the right identity);
    // a same-session token REFRESH does not — the gateway checks the token at
    // `hello` only, and a reconnect re-sends the whole board for nothing.
    useAuthStore.subscribe((s, prev) => { if (s.token !== prev.token && (s.token == null || prev.token == null)) client.forceReconnect(); }),
  ];
  publishHeld();
  client.start();
}

export function stopGateway(): void {
  _shellOwned = false;
  if (_laneRefs === 0) close();
}

function close(): void {
  if (!_client) return;
  setFeedNeedsSink(null);
  setBoardKindsSink(null);
  setRowNeedsSink(null);
  for (const d of _detach) d();
  _detach = [];
  store().noteUsFeedStatus(GATEWAY_FEED_ID, false);
  store().setFeedClients([]);
  _client.stop();
  _client = null;
  setGatewayClaims(null);
  _opKeys = null;
  _needSports = new Set();
  _needTickers = [];
  _rowTickers = [];
}
